import {useEffect, useMemo, useState} from 'react'
import {Button, Form, Modal} from 'react-bootstrap'
import {
  CampaignChannel,
  CampaignSchedule,
  ICampaign,
  IMessageTemplate,
} from '../../../types/campaigns'
import {ChannelBadge} from './ChannelBadge'
import {RecipientSelectorModal} from './RecipientSelectorModal'

export type CampaignFormValues = {
  name: string
  description?: string
  selectedVariantIds: string[] // Changed from enabledChannels
  scheduleType: CampaignSchedule
  scheduledTime?: string | null
  recurringRule?: string | null
  templateId?: string | null
  metadata?: Record<string, any>
  targetSegment?: Record<string, any>
  recipientContactIds: string[] // Campaign recipients
}

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (values: CampaignFormValues) => Promise<void>
  templates: IMessageTemplate[]
  loading?: boolean
  campaign?: ICampaign | null
}

const channelOptions: CampaignChannel[] = ['SMS', 'EMAIL', 'WHATSAPP', 'TELEGRAM']

const toInputDate = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (num: number) => `${num}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

export const CampaignFormModal = ({
  show,
  onHide,
  onSubmit,
  templates,
  loading,
  campaign,
}: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [scheduleType, setScheduleType] = useState<CampaignSchedule>('IMMEDIATE')
  const [scheduledTime, setScheduledTime] = useState('')
  const [recurringRule, setRecurringRule] = useState('')
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])
  const [templateId, setTemplateId] = useState('')
  const [metadataJson, setMetadataJson] = useState('{}')
  const [targetSegmentJson, setTargetSegmentJson] = useState('{}')
  const [recipientContactIds, setRecipientContactIds] = useState<string[]>([])
  const [showRecipientModal, setShowRecipientModal] = useState(false)

  useEffect(() => {
    if (!campaign) {
      setName('')
      setDescription('')
      setScheduleType('IMMEDIATE')
      setScheduledTime('')
      setRecurringRule('')
      setSelectedVariantIds([])
      setTemplateId('')
      setMetadataJson('{}')
      setTargetSegmentJson('{}')
      setRecipientContactIds([])
      setShowRecipientModal(false)
      return
    }
    setName(campaign.name)
    setDescription(campaign.description ?? '')
    setScheduleType(campaign.scheduleType as CampaignSchedule)
    setScheduledTime(toInputDate(campaign.scheduledTime))
    setRecurringRule(campaign.recurringRule ?? '')
    // TODO: Set selected variant IDs from campaign data when backend supports it
    setSelectedVariantIds([])
    setTemplateId(campaign.template?.id ?? '')
    setMetadataJson(JSON.stringify(campaign.metadata ?? {}, null, 2))
    setTargetSegmentJson(JSON.stringify(campaign.targetSegment ?? {}, null, 2))
    // TODO: Load recipient contact IDs from campaign when backend supports it
    setRecipientContactIds([])
  }, [campaign])

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId),
    [templates, templateId]
  )

  const toggleVariant = (variantId: string) => {
    setSelectedVariantIds((prev) => {
      if (prev.includes(variantId)) {
        return prev.filter((id) => id !== variantId)
      }
      return [...prev, variantId]
    })
  }

  // Get selected template's variants
  const availableVariants = selectedTemplate?.variants || []

  const handleSubmit = async () => {
    const parseSafe = (value: string) => {
      try {
        const parsed = JSON.parse(value || '{}')
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        return {}
      }
    }
    const payload: CampaignFormValues = {
      name,
      description,
      scheduleType,
      scheduledTime: scheduledTime ? new Date(scheduledTime).toISOString() : null,
      recurringRule,
      selectedVariantIds,
      templateId: templateId || null,
      metadata: parseSafe(metadataJson),
      targetSegment: parseSafe(targetSegmentJson),
      recipientContactIds,
    }
    await onSubmit(payload)
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{campaign ? 'Edit campaign' : 'Create campaign'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row g-4'>
            <div className='col-md-6'>
              <Form.Group className='mb-4'>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder='Fall outreach - Q4'
                />
              </Form.Group>
              <Form.Group className='mb-4'>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </Form.Group>
            </div>
            <div className='col-md-6'>
              <Form.Group className='mb-4'>
                <Form.Label>Template</Form.Label>
                <Form.Select
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                >
                  <option value=''>Select a template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Form.Select>
                {selectedTemplate && (
                  <div className='mt-2 text-muted small'>{selectedTemplate.description}</div>
                )}
              </Form.Group>
              <Form.Group className='mb-4'>
                <Form.Label>Schedule type</Form.Label>
                <Form.Select
                  value={scheduleType}
                  onChange={(event) => setScheduleType(event.target.value as CampaignSchedule)}
                >
                  <option value='IMMEDIATE'>Immediate</option>
                  <option value='SCHEDULED'>Scheduled</option>
                  <option value='RECURRING'>Recurring</option>
                </Form.Select>
              </Form.Group>
              {scheduleType !== 'IMMEDIATE' && (
                <Form.Group className='mb-4'>
                  <Form.Label>
                    {scheduleType === 'SCHEDULED' ? 'Scheduled time' : 'Next run time'}
                  </Form.Label>
                  <Form.Control
                    type='datetime-local'
                    value={scheduledTime}
                    onChange={(event) => setScheduledTime(event.target.value)}
                  />
                </Form.Group>
              )}
              {scheduleType === 'RECURRING' && (
                <Form.Group className='mb-4'>
                  <Form.Label>Recurring rule / notes</Form.Label>
                  <Form.Control
                    placeholder='e.g. every Monday at 09:00 AM'
                    value={recurringRule}
                    onChange={(event) => setRecurringRule(event.target.value)}
                  />
                </Form.Group>
              )}
              {/* <Form.Group className='mb-4'>
              <Form.Label>Metadata (JSON)</Form.Label>
              <Form.Control as='textarea' rows={4} value={metadataJson} onChange={(event) => setMetadataJson(event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Target segment (JSON)</Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                value={targetSegmentJson}
                onChange={(event) => setTargetSegmentJson(event.target.value)}
              />
            </Form.Group> */}
            </div>
            {/* Show available variants when template is selected */}
            {templateId && availableVariants.length > 0 && (
              <Form.Group className='mb-4'>
                <Form.Label>Available Variants</Form.Label>
                <div
                  className='border rounded p-3 d-flex flex-wrap gap-3'
                  style={{maxHeight: '300px', overflowY: 'auto'}}
                >
                  {availableVariants.map((variant) => {
                    const checked = selectedVariantIds.includes(variant.id)

                    return (
                      <Form.Check
                        type='checkbox'
                        key={variant.id}
                        id={`variant-${variant.id}`}
                        className={`variant-card ${checked ? 'variant-card--selected' : ''}`}
                        checked={checked}
                        onChange={() => toggleVariant(variant.id)}
                        label={
                          <div>
                            <div className='d-flex align-items-center mb-1'>
                              <ChannelBadge channel={variant.channel} />
                              <span className='ms-2 fw-bold'>{variant.channel}</span>
                              {variant.isDefault && (
                                <span className='badge bg-success ms-2'>Default</span>
                              )}
                            </div>

                            {variant.subject && (
                              <div className='text-muted small mb-1'>
                                <strong>Subject:</strong> {variant.subject}
                              </div>
                            )}

                            <div
                              className='text-muted small'
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '220px',
                              }}
                            >
                              {variant.bodyText.substring(0, 100)}
                              {variant.bodyText.length > 100 && '...'}
                            </div>
                          </div>
                        }
                      />
                    )
                  })}
                </div>
                <Form.Text className='text-muted'>
                  Select which variants to use for this campaign
                </Form.Text>
              </Form.Group>
            )}

            {templateId && availableVariants.length === 0 && (
              <div className='alert alert-warning small'>
                <i className='bi bi-exclamation-triangle me-2'></i>
                This template has no variants configured. Please add variants to the template first.
              </div>
            )}

            {/* Recipients Summary */}
            <Form.Group className='mb-4'>
              <Form.Label>Recipients</Form.Label>
              <div className='card'>
                <div className='card-body'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      {recipientContactIds.length > 0 ? (
                        <>
                          <h5 className='mb-1'>
                            <i className='bi bi-people-fill text-primary me-2'></i>
                            {recipientContactIds.length} contact
                            {recipientContactIds.length !== 1 && 's'} selected
                          </h5>
                          <small className='text-muted'>Ready to receive campaign</small>
                        </>
                      ) : (
                        <>
                          <h6 className='mb-1 text-muted'>
                            <i className='bi bi-people me-2'></i>
                            No recipients selected
                          </h6>
                          <small className='text-muted'>
                            Click the button to select recipients
                          </small>
                        </>
                      )}
                    </div>
                    <Button variant='outline-primary' onClick={() => setShowRecipientModal(true)}>
                      <i className='bi bi-person-plus-fill me-2'></i>
                      Manage Recipients
                    </Button>
                  </div>
                </div>
              </div>
              <Form.Text className='text-muted'>
                Select contacts who will receive this campaign
              </Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={
              loading ||
              !name ||
              !templateId ||
              selectedVariantIds.length === 0 ||
              recipientContactIds.length === 0
            }
          >
            {campaign ? 'Save changes' : 'Create campaign'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recipient Selector Modal */}
      <RecipientSelectorModal
        show={showRecipientModal}
        onHide={() => setShowRecipientModal(false)}
        initialSelectedIds={recipientContactIds}
        onSave={(ids) => {
          setRecipientContactIds(ids)
          setShowRecipientModal(false)
        }}
      />
    </>
  )
}
